import { supabase } from "@/integrations/supabase/client";

// Function to check if a user has completed onboarding
export async function checkOnboardingStatus(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("user_subscriptions")
    .select("is_onboarded")
    .eq("user_id", userId)
    .single();

  if (error) {
    console.error("Error checking onboarding status:", error);
    return false;
  }

  return data?.is_onboarded ?? false;
}

export async function isUserSubscribed(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("user_subscriptions")
    .select("status")
    .eq("user_id", userId)
    .maybeSingle(); // for a fresh signup there is no subscription entry yet

  if (error) {
    console.error("Error checking subscription status:", error);
    return false;
  }

  return data?.status === "active";
}

// Function to get a user's username
export async function getUserUsername(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("user_subscriptions")
    .select("username")
    .eq("user_id", userId)
    .single();

  if (error) {
    console.error("Error getting username:", error);
    return null;
  }

  return data?.username ?? null;
}

export type PreferenceType = "saved" | "completed" | "uninterested";

// Generic function to get tutorial IDs by preference type
async function getTutorialIdsByPreference(
  userId: string,
  preferenceType: PreferenceType
): Promise<string[]> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("document_id")
    .eq("user_id", userId)
    .eq("preference_type", preferenceType);

  if (error) {
    console.error(`Error fetching ${preferenceType} tutorials:`, error);
    return [];
  }

  return data.map((item) => item.document_id);
}

// Generic function to update tutorial preference
async function updateTutorialPreference(
  userId: string,
  documentId: string,
  preferenceType: PreferenceType,
  shouldAdd: boolean
) {
  if (shouldAdd) {
    const { error } = await supabase.from("tutorial_preferences").upsert({
      user_id: userId,
      document_id: documentId,
      preference_type: preferenceType,
      created_at: new Date().toISOString(),
    });

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("tutorial_preferences")
      .delete()
      .match({
        user_id: userId,
        document_id: documentId,
        preference_type: preferenceType,
      });

    if (error) throw error;
  }
}

// Saved tutorials functions
export async function getAllSavedTutorialIds(
  userId: string
): Promise<string[]> {
  return getTutorialIdsByPreference(userId, "saved");
}

export async function saveTutorial(userId: string, documentId: string) {
  return updateTutorialPreference(userId, documentId, "saved", true);
}

export async function unsaveTutorial(userId: string, documentId: string) {
  return updateTutorialPreference(userId, documentId, "saved", false);
}

export async function checkIfTutorialSaved(
  userId: string,
  documentId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("*")
    .match({
      user_id: userId,
      document_id: documentId,
      preference_type: "saved",
    })
    .single();

  if (error && error.code !== "PGRST116") {
    // PGRST116 is the "no rows returned" error
    console.error("Error checking if tutorial is saved:", error);
    return false;
  }

  return !!data;
}

// Completed tutorials functions
export async function getCompletedTutorialIds(
  userId: string
): Promise<string[]> {
  return getTutorialIdsByPreference(userId, "completed");
}

export async function markTutorialAsCompleted(
  userId: string,
  documentId: string
) {
  return updateTutorialPreference(userId, documentId, "completed", true);
}

export async function unmarkTutorialAsCompleted(
  userId: string,
  documentId: string
) {
  return updateTutorialPreference(userId, documentId, "completed", false);
}

export async function checkTutorialCompletion(
  userId: string,
  documentId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("*")
    .match({
      user_id: userId,
      document_id: documentId,
      preference_type: "completed",
    });

  if (error) {
    console.error("Error checking tutorial completion:", error);
    return false;
  }

  return data && data.length > 0;
}

export async function getTutorialsByDocuments(
  userId: string,
  documentIds: string[]
): Promise<any[]> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("*")
    .match({
      user_id: userId,
    })
    .in("document_id", documentIds);

  if (error) {
    console.error("Error fetching tutorials:", error);
    return [];
  }

  return data || [];
}

// New functions for uninterested tutorials
export async function getUninterestedTutorialIds(
  userId: string
): Promise<string[]> {
  return getTutorialIdsByPreference(userId, "uninterested");
}

export async function markTutorialAsUninterested(
  userId: string,
  documentId: string
) {
  return updateTutorialPreference(userId, documentId, "uninterested", true);
}

export async function unmarkTutorialAsUninterested(
  userId: string,
  documentId: string
) {
  const { error } = await supabase
    .from("tutorial_preferences")
    .delete()
    .eq("user_id", userId)
    .eq("document_id", documentId)
    .eq("preference_type", "uninterested");

  if (error) throw error;
}

// Function to get all preferences for a tutorial
export async function getTutorialPreferences(
  userId: string,
  documentId: string
): Promise<PreferenceType[]> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("preference_type")
    .match({
      user_id: userId,
      document_id: documentId,
    });

  if (error) {
    console.error("Error fetching tutorial preferences:", error);
    return [];
  }

  return data.map((item) => item.preference_type as PreferenceType);
}

// Add this function to get the save count for a tutorial
export async function getTutorialSaveCount(
  documentId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("tutorial_preferences")
    .select("*", { count: "exact", head: true })
    .eq("document_id", documentId)
    .eq("preference_type", "saved");

  if (error) {
    console.error("Error fetching tutorial save count:", error);
    return 0;
  }

  return count || 0;
}

// Get all saved tutorials for a user
export async function getUserSavedTutorials(userId: string) {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("document_id")
    .eq("user_id", userId)
    .eq("preference_type", "saved")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching saved tutorials:", error);
    return [];
  }

  return data.map((item) => item.document_id);
}

// Add these types and functions for tag preferences
export type TagPreferenceType = "included" | "excluded";

interface UserTagPreference {
  user_id: string;
  tag_name: string;
  preference_type: TagPreferenceType;
  created_at: string;
  updated_at: string;
}

export async function getUserTagPreferences(userId: string) {
  const { data, error } = await supabase
    .from("user_tag_preferences")
    .select("*")
    .eq("user_id", userId);

  if (error) {
    console.error("Error fetching user tag preferences:", error);
    return null;
  }

  // Initialize empty arrays for preferences
  const preferences = {
    included: [] as string[],
    excluded: [] as string[],
  };

  // Only process data if there are any preferences
  if (data && data.length > 0) {
    data.forEach((pref) => {
      if (pref.preference_type === "included") {
        preferences.included.push(pref.tag_name);
      } else {
        preferences.excluded.push(pref.tag_name);
      }
    });
  }

  return preferences;
}

export async function updateUserTagPreference(
  userId: string,
  tagName: string,
  preferenceType: TagPreferenceType
) {
  const { error } = await supabase.from("user_tag_preferences").upsert(
    {
      user_id: userId,
      tag_name: tagName,
      preference_type: preferenceType,
      updated_at: new Date().toISOString(),
    },
    {
      // These are the columns that make up our unique constraint
      onConflict: "user_id,tag_name",
      // Update these fields if there's a conflict
      ignoreDuplicates: false,
    }
  );

  if (error) {
    console.error("Error updating tag preference:", error);
    throw error;
  }
}

export async function deleteUserTagPreference(userId: string, tagName: string) {
  const { error } = await supabase.from("user_tag_preferences").delete().match({
    user_id: userId,
    tag_name: tagName,
  });

  if (error) {
    console.error("Error deleting tag preference:", error);
    throw error;
  }
}

export async function checkTutorialUninterested(
  userId: string,
  documentId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("*")
    .match({
      user_id: userId,
      document_id: documentId,
      preference_type: "uninterested",
    });

  if (error) {
    console.error("Error checking tutorial uninterested state:", error);
    return false;
  }

  return data && data.length > 0;
}

export type ToolPreferenceType = "included" | "excluded";

export interface ToolPreferences {
  included: string[];
  excluded: string[];
}

export async function getUserToolPreferences(
  userId: string
): Promise<ToolPreferences | null> {
  const { data, error } = await supabase
    .from("user_tool_preferences")
    .select("tool_name, preference_type")
    .eq("user_id", userId);

  if (error) throw error;

  if (!data) return null;

  return data.reduce<ToolPreferences>(
    (acc, { tool_name, preference_type }) => {
      if (preference_type === "included") {
        acc.included.push(tool_name);
      } else if (preference_type === "excluded") {
        acc.excluded.push(tool_name);
      }
      return acc;
    },
    { included: [], excluded: [] }
  );
}

export async function updateUserToolPreference(
  userId: string,
  toolName: string,
  preferenceType: ToolPreferenceType
): Promise<void> {
  const { error } = await supabase.from("user_tool_preferences").upsert(
    {
      user_id: userId,
      tool_name: toolName,
      preference_type: preferenceType,
    },
    {
      onConflict: "user_id,tool_name",
      ignoreDuplicates: false,
    }
  );

  if (error) {
    console.error("Error updating tool preference:", error);
    throw error;
  }
}

export async function deleteUserToolPreference(
  userId: string,
  toolName: string
): Promise<void> {
  const { error } = await supabase
    .from("user_tool_preferences")
    .delete()
    .eq("user_id", userId)
    .eq("tool_name", toolName);

  if (error) throw error;
}

// Add this function to get the completed count for a tutorial
export async function getTutorialCompletedCount(
  documentId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("tutorial_preferences")
    .select("*", { count: "exact", head: true })
    .eq("document_id", documentId)
    .eq("preference_type", "completed");

  if (error) {
    console.error("Error fetching tutorial completed count:", error);
    return 0;
  }

  return count || 0;
}

// Tutorial ratings functions
export async function setTutorialRating(
  userId: string,
  documentId: string,
  rating: number
) {
  const { error } = await supabase.from("tutorial_ratings").upsert({
    user_id: userId,
    document_id: documentId,
    rating,
    updated_at: new Date().toISOString(),
  });

  if (error) throw error;
}

export async function getUserTutorialRating(
  userId: string,
  documentId: string
): Promise<number | null> {
  const { data, error } = await supabase
    .from("tutorial_ratings")
    .select("rating")
    .match({ user_id: userId, document_id: documentId });

  if (error) {
    console.error("Error fetching user tutorial rating:", error);
    throw error;
  }

  return data && data.length > 0 ? data[0].rating : null;
}

export async function getTutorialAverageRating(
  documentId: string
): Promise<number> {
  const { data, error } = await supabase
    .from("tutorial_ratings")
    .select("rating")
    .eq("document_id", documentId);

  if (error) {
    console.error("Error fetching tutorial average rating:", error);
    return 0;
  }

  if (!data || data.length === 0) return 0;

  const sum = data.reduce((acc, curr) => acc + curr.rating, 0);
  const average = sum / data.length;
  return Math.round(average);
}

export async function deleteTutorialRating(userId: string, documentId: string) {
  const { error } = await supabase
    .from("tutorial_ratings")
    .delete()
    .match({ user_id: userId, document_id: documentId });

  if (error) throw error;
}

// Function to check if a user's profile is scheduled for deletion
export async function isProfileScheduledForDeletion(
  userId: string
): Promise<boolean> {
  const {
    data: { user },
    error,
  } = await supabase.auth.admin.getUserById(userId);

  if (error || !user) return false;

  const deletedAt = user.user_metadata?.deleted_at;
  if (!deletedAt) return false;

  const deletionDate = new Date(deletedAt);
  const now = new Date();
  return now.getTime() - deletionDate.getTime() < 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
}

// Function to restore a user's profile
export async function restoreProfile(
  userId: string
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.auth.admin.updateUserById(userId, {
      user_metadata: { deleted_at: null },
    });

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}

// Function to permanently delete a user's profile after 30 days
export async function permanentlyDeleteProfile(
  userId: string
): Promise<{ error: Error | null }> {
  try {
    const isScheduled = await isProfileScheduledForDeletion(userId);
    if (!isScheduled) {
      const { error } = await supabase.auth.admin.deleteUser(userId);
      if (error) throw error;
    }

    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}

export async function getAllTutorialRatings(): Promise<
  { id: string; rating: number }[]
> {
  const { data, error } = await supabase
    .from("tutorial_ratings")
    .select("document_id, rating");

  if (error) {
    console.error("Error fetching all tutorial ratings:", error);
    return [];
  }

  // Group by document_id and calculate average rating
  const ratingsByTutorial = data.reduce<Record<string, number[]>>(
    (acc, { document_id, rating }) => {
      if (!acc[document_id]) {
        acc[document_id] = [];
      }
      acc[document_id].push(rating);
      return acc;
    },
    {}
  );

  return Object.entries(ratingsByTutorial).map(([id, ratings]) => ({
    id,
    rating: Math.round(ratings.reduce((sum, r) => sum + r, 0) / ratings.length),
  }));
}

export async function getAllTutorialCompletedCounts(): Promise<
  { id: string; count: number }[]
> {
  const { data, error } = await supabase
    .from("tutorial_preferences")
    .select("document_id")
    .eq("preference_type", "completed");

  if (error) {
    console.error("Error fetching all tutorial completed counts:", error);
    return [];
  }

  // Count occurrences of each document_id
  const countsByTutorial = data.reduce<Record<string, number>>(
    (acc, { document_id }) => {
      acc[document_id] = (acc[document_id] || 0) + 1;
      return acc;
    },
    {}
  );

  return Object.entries(countsByTutorial).map(([id, count]) => ({
    id,
    count,
  }));
}
